/**
 * Press Tiles Section
 * - Filter: two-group AND filtering (content type + model) for grid mode
 * - URL param support: ?type=youtube&model=d-1+diver pre-selects filters
 * - Carousel: Swiper init for carousel mode (uses existing swiper-bundle.min.js)
 * - Completely isolated, no global side effects
 */

if (!customElements.get('press-tiles-section')) {
  class PressTilesSection extends HTMLElement {
    constructor() {
      super();
      this.initFilter();
      this.initCarousel();

      window.addEventListener('shopify:section:load', () => {
        this.initFilter();
        this.initCarousel();
      });
    }

    /* ------------------------------------------------------------------
       Filter (Grid / Press Page mode)
       Two filter groups: "type" (Press/YouTube/Podcast) and "model" (D-1/P-1/etc.)
       Items must match BOTH active filters (AND logic)
       ------------------------------------------------------------------ */

    initFilter() {
      const filterBars = this.querySelectorAll('[data-press-filter]');
      if (!filterBars.length) return;

      const grid = this.querySelector('[data-press-grid]');
      if (!grid) return;

      const modelBar = this.querySelector('[data-press-filter="model"]');
      const typeBar = this.querySelector('[data-press-filter="type"]');

      /* Default to first type filter button (Press) instead of "all" */
      const firstTypeBtn = typeBar ? typeBar.querySelector('[data-filter]') : null;
      const activeFilters = { type: firstTypeBtn ? firstTypeBtn.dataset.filter : 'all', model: 'all' };

      const applyFilters = () => {
        /* Hide model filters when YouTube or Podcasts is active (too few items) */
        if (modelBar) {
          const showModelBar = activeFilters.type === 'all' || activeFilters.type === 'press';
          modelBar.hidden = !showModelBar;
          if (!showModelBar) activeFilters.model = 'all';
        }

        grid.querySelectorAll('[data-model]').forEach((item) => {
          const modelData = item.dataset.model || '';
          const typeData = item.dataset.type || '';

          const matchesType = activeFilters.type === 'all' || typeData.includes(activeFilters.type);
          const matchesModel = activeFilters.model === 'all' || modelData.includes(activeFilters.model);

          item.hidden = !(matchesType && matchesModel);
        });
      };

      filterBars.forEach((bar) => {
        const group = bar.dataset.pressFilter; // "type" or "model"

        bar.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-filter]');
          if (!btn) return;

          /* Model filters: toggle (click again = deselect = show all) */
          if (group === 'model' && activeFilters.model === btn.dataset.filter) {
            activeFilters.model = 'all';
            bar.querySelectorAll('[data-filter]').forEach((b) => b.classList.remove('is-active'));
          } else {
            activeFilters[group] = btn.dataset.filter;
            bar.querySelectorAll('[data-filter]').forEach((b) => {
              b.classList.toggle('is-active', b.dataset.filter === activeFilters[group]);
            });
          }

          applyFilters();
        });
      });

      /* URL param support: ?type=youtube&model=d-1+diver */
      const params = new URLSearchParams(window.location.search);
      const urlType = params.get('type');
      const urlModel = params.get('model') || params.get('filter');

      if (urlType) {
        activeFilters.type = urlType.toLowerCase();
        filterBars.forEach((bar) => {
          if (bar.dataset.pressFilter === 'type') {
            bar.querySelectorAll('[data-filter]').forEach((b) => {
              b.classList.toggle('is-active', b.dataset.filter === activeFilters.type);
            });
          }
        });
      }

      if (urlModel) {
        activeFilters.model = urlModel.toLowerCase();
        filterBars.forEach((bar) => {
          if (bar.dataset.pressFilter === 'model') {
            bar.querySelectorAll('[data-filter]').forEach((b) => {
              b.classList.toggle('is-active', b.dataset.filter === activeFilters.model);
            });
          }
        });
      }

      if (urlType || urlModel) {
        applyFilters();
      }

      /* Always apply initial filter (Press is default, not "all") */
      applyFilters();
    }

    /* ------------------------------------------------------------------
       Carousel (Product Page mode)
       ------------------------------------------------------------------ */

    initCarousel() {
      const swiperEl = this.querySelector('[data-press-swiper]');
      if (!swiperEl) return;

      const options = JSON.parse(swiperEl.getAttribute('data-swiper-options') || '{}');
      const sectionId = options.sectionId || '';
      const slideCount = swiperEl.querySelectorAll('.swiper-slide').length;

      const swiperConfig = {
        slidesPerView: 1.15,
        spaceBetween: 12,
        resistanceRatio: 0.72,
        loop: slideCount > 3 && (options.loop || false),
        navigation: {
          prevEl: `.press-tiles__arrow--prev-${sectionId}`,
          nextEl: `.press-tiles__arrow--next-${sectionId}`
        },
        breakpoints: {
          575: {
            slidesPerView: 2,
            spaceBetween: 16
          },
          750: {
            slidesPerView: options.slidesPerViewDesktop || 3,
            spaceBetween: options.spaceBetweenDesktop || 16
          }
        }
      };

      if (options.autoplay && slideCount > (options.slidesPerViewDesktop || 3)) {
        swiperConfig.autoplay = {
          delay: options.autoplay.delay || 6000,
          disableOnInteraction: false
        };
      }

      if (this.swiper) {
        this.swiper.destroy(true, true);
      }

      this.swiper = new Swiper(swiperEl, swiperConfig);

      /* Disable controls when fewer slides than visible */
      if (slideCount <= (options.slidesPerViewDesktop || 3)) {
        this.classList.add('press-tiles--few-items');
      } else {
        this.classList.remove('press-tiles--few-items');
      }
    }
  }

  customElements.define('press-tiles-section', PressTilesSection);
}
